import { ArrowDown, ArrowUp } from 'lucide-react';
import Podium from '../Podium/Podium';
import { abbreviateName, formatAum, formatCurrency, formatNumber, formatPercent } from '../../utils/format';
import styles from './TraderTable.module.css';

const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

// Below `md` the table narrows to Rank / Name / Net withdrawals and the top
// three move into <Podium>, so those columns and rows are hidden by utilities
// rather than by branching on a JS breakpoint.
const MOBILE_HIDDEN = 'd-none d-md-table-cell';

const COLUMNS = [
  { key: 'rank', label: 'Rank', align: 'start' },
  { key: 'name', label: 'Trader', mobileLabel: 'Name', align: 'start' },
  { key: 'totalAum', label: 'Total AUM', align: 'start', hideOnMobile: true },
  { key: 'activeAccounts', label: 'Active accounts', align: 'center', hideOnMobile: true },
  { key: 'netWithdrawals', label: 'Net withdrawals', align: 'end', sortable: true },
  { key: 'returnOnFees', label: 'Return on fees', align: 'end', sortable: true, hideOnMobile: true },
];

function TraderTable({ traders, loading, error, sort, onSort }) {
  // The podium is the top three of whatever order is on screen, so sorting a
  // column re-seats it. Mobile only, where the response is never paginated and
  // the first three rows are therefore the true top three.
  const podium = traders.slice(0, 3);
  const showPodium = podium.length === 3;
  const message = loading ? 'Loading traders…' : error || 'No traders yet';

  return (
    <>
      {showPodium && (
        <div className="d-md-none">
          <Podium traders={podium} />
        </div>
      )}

      <div className={`${styles.card} table-responsive`}>
        <table className={`${styles.table} table mb-0`}>
          <thead>
            <tr>
              {COLUMNS.map((column) => {
                const isSorted = sort && sort.key === column.key;
                const SortIcon = isSorted && sort.direction === 'asc' ? ArrowUp : ArrowDown;
                const label = column.mobileLabel ? (
                  <>
                    <span className="d-md-none">{column.mobileLabel}</span>
                    <span className="d-none d-md-inline">{column.label}</span>
                  </>
                ) : (
                  column.label
                );

                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={`${styles.th} text-${column.align} ${column.hideOnMobile ? MOBILE_HIDDEN : ''}`}
                    aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        onClick={() => onSort(column.key)}
                        className={`${styles.sortButton} ${isSorted ? styles.sortActive : ''} d-inline-flex align-items-center gap-2`}
                      >
                        {label}
                        <SortIcon size={18} strokeWidth={2} aria-hidden="true" />
                      </button>
                    ) : (
                      label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {traders.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className={`${styles.td} ${styles.empty} text-center`}>
                  {message}
                </td>
              </tr>
            ) : (
              traders.map((trader, index) => (
                <tr
                  key={trader.userId}
                  // The top three are shown by <Podium> on mobile instead.
                  className={showPodium && index < 3 ? 'd-none d-md-table-row' : undefined}
                >
                  <td className={`${styles.td} ${styles.rankCell}`}>
                    <span className="d-inline-flex align-items-center gap-2">
                      {/* Desktop only: on mobile the medals belong to the podium,
                          and a sorted list can strand rank 1-3 mid-table. */}
                      {MEDALS[trader.rank] && (
                        <span className="d-none d-md-inline" aria-hidden="true">
                          {MEDALS[trader.rank]}
                        </span>
                      )}
                      <span className={trader.rank <= 3 ? styles.rankTop : undefined}>
                        {trader.rank}
                      </span>
                    </span>
                  </td>
                  <td className={styles.td}>{abbreviateName(trader.name)}</td>
                  <td className={`${styles.td} ${MOBILE_HIDDEN}`}>{formatAum(trader.totalAum)}</td>
                  <td className={`${styles.td} ${MOBILE_HIDDEN} text-center`}>
                    {formatNumber(trader.activeAccounts)}
                  </td>
                  <td className={`${styles.td} text-end`}>{formatCurrency(trader.netWithdrawals)}</td>
                  <td className={`${styles.td} ${styles.positive} ${MOBILE_HIDDEN} text-end`}>
                    {formatPercent(trader.returnOnFees)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TraderTable;
